import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../ui/Button';
import Lightbox from '../../ui/Lightbox';
import AnimatedSection from '../../common/AnimatedSection';
import communiquesData from '../../../data/communiquesData.json';
import { ArrowRightIcon, CalendarIcon } from '@heroicons/react/24/outline';

export default function CommuniquesHomeSection() {
  const [communiques, setCommuniques] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    // 1. Initialiser avec les données locales triées par date récente
    const sortedLocal = [...communiquesData].sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );
    setCommuniques(sortedLocal.slice(0, 3));

    // 2. Synchroniser avec le repo GitHub si disponible
    fetch(`https://raw.githubusercontent.com/acemfes/acem_fes_communiques/main/communiquesData.json?t=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        data.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setCommuniques(data.slice(0, 3));
      })
      .catch((err) => console.error("Erreur de synchronisation des communiqués:", err));
  }, []);

  const getMediaUrl = (item) => {
    if (item.imageFiles && item.imageFiles.length > 0) {
      return `/communiques/${item.imageFiles[0]}`;
    }
    return item.imageUrl || '/logo.png';
  };

  const getGithubFallback = (item) => {
    if (item.imageFiles && item.imageFiles.length > 0) {
      return `https://raw.githubusercontent.com/acemfes/acem_fes_communiques/main/images/${encodeURIComponent(item.imageFiles[0])}`;
    }
    return item.imageUrl || '/logo.png';
  };

  if (!communiques || communiques.length === 0) return null;

  return (
    <AnimatedSection animation="stagger" selector=".communique-card" stagger={0.15} className="py-8 max-w-7xl mx-auto px-4">
      {/* En-tête de section centré */}
      <div className="text-center mb-10 space-y-2 max-w-2xl mx-auto">
        <h2 className="font-title text-2xl sm:text-3xl md:text-4xl font-extrabold text-acem-primary-dark">
          Derniers Communiqués
        </h2>
        <p className="text-sm sm:text-base text-acem-muted max-w-xl mx-auto">
          Restez informés des annonces officielles, événements et décisions importantes de l'ACEM Fès.
        </p>
      </div>

      {/* Grille des 3 derniers communiqués avec dégradé et texte incrusté */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        {communiques.map((item) => {
          const imgSrc = getMediaUrl(item);
          const fallbackSrc = getGithubFallback(item);

          return (
            <div
              key={item.id}
              onClick={(e) => {
                const imgEl = e.currentTarget.querySelector('img');
                const activeSrc = (imgEl && imgEl.currentSrc) || (imgEl && imgEl.src) || fallbackSrc || imgSrc;
                setSelectedImage(activeSrc);
              }}
              className="communique-card relative group rounded-2xl sm:rounded-3xl overflow-hidden shadow-acem-md hover:shadow-acem-xl transition-all duration-500 cursor-pointer border border-acem-border/60 bg-acem-surface-container-low aspect-[4/5] flex flex-col justify-end"
              title="Cliquer pour agrandir"
            >
              {/* Image principale */}
              <img
                src={imgSrc}
                alt={item.titre}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                onError={(e) => {
                  if (e.currentTarget.src !== fallbackSrc) {
                    e.currentTarget.src = fallbackSrc;
                  }
                }}
              />

              {/* Dégradé du bas vers le haut */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent transition-opacity duration-300 group-hover:from-black/90" />

              {/* Contenu textuel incrusté dans le dégradé */}
              <div className="relative z-10 p-5 sm:p-6 space-y-2.5 text-white transform transition-transform duration-300 group-hover:-translate-y-1">
                {/* Catégorie & Date */}
                <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                  <span className="px-3 py-1 rounded-full font-bold bg-acem-primary/90 text-white backdrop-blur-md shadow-sm uppercase tracking-wider text-[11px]">
                    {item.categorie}
                  </span>
                  <span className="text-white/80 font-medium flex items-center gap-1.5 backdrop-blur-sm bg-black/30 px-2.5 py-1 rounded-full text-[11px]">
                    <CalendarIcon className="w-3.5 h-3.5 text-acem-accent" />
                    {item.dateFormatee || item.date}
                  </span>
                </div>

                {/* Titre */}
                <h3 className="font-title text-lg sm:text-xl font-bold text-white leading-snug line-clamp-2 group-hover:text-amber-300 transition-colors drop-shadow-sm">
                  {item.titre}
                </h3>

                {/* Description snippet */}
                {item.description && (
                  <p className="text-xs text-white/75 line-clamp-2 font-light leading-relaxed drop-shadow-sm">
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bouton Voir tous les communiqués positionné en bas à droite */}
      <div className="flex justify-end pt-6">
        <Button
          as={Link}
          to="/evenements/communiques"
          variant="ghost"
          size="md"
          className="inline-flex items-center gap-2 text-acem-primary hover:text-acem-primary-dark font-bold group"
        >
          <span>Voir tous les communiqués</span>
          <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
        </Button>
      </div>

      {/* Lightbox pour agrandissement */}
      {selectedImage && (
        <Lightbox
          src={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </AnimatedSection>
  );
}

